import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  Grid,
  Alert,
  Skeleton,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchAuditLogs } from '../../../store/slices/superAdminSlice';

const AuditLogsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { auditLogs, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchData = useCallback(() => {
    dispatch(fetchAuditLogs({
      searchTerm: searchTerm || undefined,
      action: actionFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    }));
  }, [dispatch, searchTerm, actionFilter, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getActionColor = (action: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const lower = action.toLowerCase();
    if (lower.includes('create') || lower.includes('add')) return 'success';
    if (lower.includes('delete') || lower.includes('remove')) return 'error';
    if (lower.includes('update') || lower.includes('edit')) return 'warning';
    if (lower.includes('login') || lower.includes('auth')) return 'info';
    return 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Audit Logs</Typography>
          <Typography variant="body2" color="text.secondary">Track all system activities</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" label="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ endAdornment: <Search color="action" /> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField fullWidth size="small" label="Action" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} placeholder="e.g., Create" />
          </Grid>
          <Grid size={{ xs: 12, sm: 2.5 }}>
            <TextField fullWidth size="small" label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2.5 }}>
            <TextField fullWidth size="small" label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {auditLogs.length}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Entity</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : auditLogs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No audit logs found</Typography></TableCell></TableRow>
            ) : (
              auditLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell><Typography variant="body2">{new Date(log.timestamp).toLocaleString()}</Typography></TableCell>
                  <TableCell><Chip label={log.action} color={getActionColor(log.action)} size="small" /></TableCell>
                  <TableCell>{log.userName}</TableCell>
                  <TableCell>{log.tenantName || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.entityType}</Typography>
                    {log.entityId && <Typography variant="caption" color="text.secondary">{log.entityId.substring(0, 8)}...</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{log.ipAddress || '—'}</Typography></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuditLogsPage;
